import { useTranslation } from 'react-i18next';
import CheckOutlined from '@ant-design/icons/lib/icons/CheckOutlined';
import axios from 'axios';
import openpathConfig from 'openpathConfig';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { getAccessToken } from 'utils/accessToken';

import { VisitorWorkflowsState } from '../../../../../store/slices/visitorWorkflowsSlice';
import { VisitsState } from '../../../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../../../store/store';
import { Agreement } from '../../../../../types/agreementTypes';
import { DocumentCheck } from '../../../../../types/visitTypes';

interface AgreementsTabProps {
  extendedVisitorInfo: DocumentCheck;
  attendantAgreements: Agreement[];
  kioskAgreements: Agreement[];
  signedAgreements: Agreement[];
}

const AgreementsTab = ({
  extendedVisitorInfo,
  attendantAgreements,
  kioskAgreements,
  signedAgreements,
}: AgreementsTabProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const selectedSignInWorkflowConfig = useSelectorJs(
    (state: ImmutableRootState) =>
      state.getIn(['visitorWorkflows', 'selectedSignInWorkflowConfig']),
  ) as VisitorWorkflowsState['selectedSignInWorkflowConfig'];

  const handleViewSignedAgreements = async () => {
    try {
      const token = getAccessToken();
      const response = await axios({
        method: 'GET',
        url: `${openpathConfig.PORTAL_API_URL}/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${selectedVisitor?.id}/agreement`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'agreements.pdf';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error: unknown) {
      const errorStatus =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { status?: number } }).response
          ?.status === 'number'
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;

      if (errorStatus === 404) {
        notificationService.show(
          'warning',
          t(
            'visitors.profile.notification.agreementNotFoundWarningDescription',
          ),
          t('visitors.profile.notification.agreementNotFoundWarning'),
        );
      }
    }
  };
  const isDisplayEnabled =
    selectedSignInWorkflowConfig?.agreementsDisplay === 1;
  const hasSignedPDF = !!extendedVisitorInfo?.agreement;

  let displayAgreements: Agreement[] = [];

  if (isDisplayEnabled) {
    displayAgreements = [...attendantAgreements, ...signedAgreements];

    if (hasSignedPDF) {
      displayAgreements = [
        ...attendantAgreements,
        ...signedAgreements,
        ...kioskAgreements,
      ];
    }
  } else if (!isDisplayEnabled) {
    displayAgreements = [...signedAgreements];

    if (hasSignedPDF) {
      displayAgreements = [...signedAgreements, ...kioskAgreements];
    }
  }

  // Remove duplicates based on id
  displayAgreements = Array.from(
    new Map(displayAgreements.map((a) => [a.id, a])).values(),
  );

  return (
    <>
      <OpList
        itemLayout="horizontal"
        dataSource={displayAgreements}
        bordered
        locale={{
          emptyText: (
            <OpEmpty description={t('visitors.profile.agreements.noData')} />
          ),
        }}
        renderItem={(item) => {
          const agreement = item as Agreement;
          return (
            <OpList.Item
              extra={
                signedAgreements.some(
                  (signedAgreement) => signedAgreement.id === agreement.id,
                ) && <CheckOutlined style={{ color: 'green' }} />
              }
            >
              <OpList.Item.Meta
                title={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {agreement.name}
                  </div>
                }
              />
            </OpList.Item>
          );
        }}
      />
      {extendedVisitorInfo?.agreement != null && (
        <OpButton
          onClick={handleViewSignedAgreements}
          type="primary"
          ghost
          style={{ width: '100%', marginTop: '10px' }}
        >
          {t('visitors.profile.agreements.viewSigned')}
        </OpButton>
      )}
    </>
  );
};

export default AgreementsTab;
